// ═══════════════════════════════════════════════════════════════
// CRM INSTASOLUTIONS — UTILITÁRIOS
// ═══════════════════════════════════════════════════════════════

import { temDocAssinante, docsComplementaresFaltando } from "../constants";

export const generateId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "id_" + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);

export const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

// dd/mm/aaaa — o formato que a equipe lê e escreve. "05 de ago. de 2026" era
// bonito e obrigava a traduzir de cabeça na hora de conferir com o cliente.
// O T12:00:00 evita o dia voltar um por fuso quando a data vem só como AAAA-MM-DD.
export const fmtDate = (d) =>
  d
    ? new Date(String(d).length === 10 ? d + "T12:00:00" : d).toLocaleDateString("pt-BR")
    : "—";

export const fmtShort = (d) =>
  d
    ? new Date(String(d).length === 10 ? d + "T12:00:00" : d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    : "—";

// Data e hora no mesmo padrão: 05/08/2026 14:22
export const fmtDataHora = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
};

export const ago = (d) => {
  if (!d) return "—";
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return "agora";
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export const getToday = () => new Date().toISOString().split("T")[0];

// Soma meses a uma data (YYYY-MM-DD). Sem argumento de data, parte de hoje.
export const addMonths = (dateStr, months) => {
  const base = dateStr ? new Date(dateStr + "T12:00:00") : new Date();
  base.setMonth(base.getMonth() + months);
  return base.toISOString().split("T")[0];
};

export const validateEmail = (email) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validatePhone = (phone) => {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 13;
};

// ── Máscaras de formatação BR ──
export const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export const maskCPF = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export const maskCNPJ = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

export const maskCEP = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
};

export const maskCurrency = (v) => {
  const d = v.replace(/\D/g, "");
  if (!d) return "";
  const num = parseInt(d, 10) / 100;
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const unmaskCurrency = (v) => {
  const d = v.replace(/\D/g, "");
  return d ? parseInt(d, 10) / 100 : 0;
};

// ── Consulta CNPJ via BrasilAPI ──
// Mapeia a resposta do CNPJá (aberto ou autenticado) para o nosso formato
const mapCnpja = (d) => {
  const addr = d.address || {};
  const phones = d.phones || [];
  const fmt = (p) => (p ? `(${p.area}) ${p.number}` : "");
  const landline = phones.find((p) => p.type === "LANDLINE") || phones[0];
  const mobile = phones.find((p) => p.type === "MOBILE");
  const members = d.company?.members || [];
  const adm = members.find((m) => /administrador|diretor|titular/i.test(m.role?.text || "")) || members[0];
  const opt = d.company?.simples?.optant;
  const reg = (d.registrations || []).find((r) => r.enabled) || (d.registrations || [])[0];
  return {
    razaoSocial: d.company?.name || "",
    nomeFantasia: d.alias || "",
    rua: addr.street || "", numero: addr.number != null ? String(addr.number) : "", bairro: addr.district || "",
    cidade: addr.city || "", uf: addr.state || "", cep: addr.zip || "",
    endereco: [addr.street, addr.number, addr.details, addr.district].filter(Boolean).join(", "),
    enderecoCompleto: [addr.street, addr.number, addr.details, addr.district, addr.city ? `${addr.city}/${addr.state}` : ""].filter(Boolean).join(", "),
    atividadePrincipal: d.mainActivity?.text || "",
    simplesNacional: opt === true ? "sim" : opt === false ? "nao" : "",
    situacao: d.status?.text || "",
    telefone: fmt(landline), celular: fmt(mobile),
    email: (d.emails && d.emails[0]?.address) || "",
    inscricaoEstadual: reg?.number || "",
    representanteNome: adm?.person?.name || "", representanteCargo: adm?.role?.text || "",
  };
};

export const fetchCNPJ = async (cnpj) => {
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return null;

  // A CHAVE DO CNPJá NÃO ENTRA AQUI.
  //
  // Havia um caminho que lia `REACT_APP_CNPJA_TOKEN` e chamava a API paga
  // direto do navegador. Toda variável REACT_APP_* é EMBUTIDA no JS público —
  // a chave ficaria legível para qualquer um que abrisse o formulário, e os
  // créditos são pré-pagos. Um F12 esvaziava a conta.
  //
  // O que precisa de chave roda no servidor (api/cnpj-comprovante.js), uma vez
  // por ficha, no momento de gravar. Aqui, enquanto a pessoa digita, vale a API
  // aberta: não custa crédito e preenche o formulário igual.

  // 1) CNPJá aberto (grátis, traz e-mail; sem Inscrição Estadual)
  try {
    const res = await fetch(`https://open.cnpja.com/office/${digits}`);
    if (res.ok) return mapCnpja(await res.json());
  } catch { /* cai pro fallback */ }

  // 2) Fallback: BrasilAPI (grátis, sem e-mail)
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${digits}`);
    if (!res.ok) return null;
    const d = await res.json();
    const socios = Array.isArray(d.qsa) ? d.qsa : [];
    const adm = socios.find((s) => /administrador/i.test(s.qualificacao_socio || "")) || socios[0];
    return {
      razaoSocial: d.razao_social || "",
      nomeFantasia: d.nome_fantasia || "",
      rua: d.logradouro || "",
      numero: d.numero ? String(d.numero) : "",
      bairro: d.bairro || "",
      endereco: [d.logradouro, d.numero, d.complemento, d.bairro].filter(Boolean).join(", "),
      cidade: d.municipio || "",
      uf: d.uf || "",
      cep: d.cep || "",
      enderecoCompleto: [d.logradouro, d.numero, d.complemento, d.bairro, d.municipio ? `${d.municipio}/${d.uf}` : ""].filter(Boolean).join(", "),
      atividadePrincipal: d.cnae_fiscal_descricao || "",
      simplesNacional: d.opcao_pelo_simples === true ? "sim" : d.opcao_pelo_simples === false ? "nao" : "",
      situacao: d.situacao_cadastral === 2 ? "Ativa" : "Inativa",
      telefone: d.ddd_telefone_1 ? `(${d.ddd_telefone_1.slice(0, 2)}) ${d.ddd_telefone_1.slice(2)}` : "",
      celular: d.ddd_telefone_2 ? `(${d.ddd_telefone_2.slice(0, 2)}) ${d.ddd_telefone_2.slice(2)}` : "",
      email: "",
      inscricaoEstadual: "",
      representanteNome: adm?.nome_socio || "", representanteCargo: adm?.qualificacao_socio || "",
    };
  } catch {
    return null;
  }
};

// ── Status/pendências de uma ficha de credenciamento ──
// Retorna { id, label, color, pend[] } derivado da completude do cadastro.
// Aceite válido: aceite dos termos (checkbox) OU rubrica antiga OU nome + documento
export const temAssinatura = (c) =>
  !!c?.aceiteTermos ||
  !!c?.assinaturaPath ||
  (!!c?.responsavelNome && (!!c?.responsavelDoc || !!c?.responsavelRg || (c?.documentos || []).length > 0));

export const credStatus = (c) => {
  if (!c) return { id: "pendente", label: "⏳ Pendente", color: "#f59e0b", pend: [], docsPend: [] };
  const pend = [];
  const assinado = temAssinatura(c);
  const docs = c.documentos || [];
  if (!assinado) pend.push("Aceite dos termos");
  // Pendência do cadastro é só o documento de quem assinou. Os da empresa saem
  // em `docsPend` — cobram na tela, mas não fazem a ficha parecer incompleta.
  if (!temDocAssinante(docs)) pend.push("Documento de quem assina");
  if (!c.cnpj) pend.push("CNPJ");
  if (!c.responsavelNome) pend.push("Responsável");
  const docsPend = docsComplementaresFaltando(docs, c);
  // Sem interesse é escolha manual da equipe: sai do fluxo de pendências e vira
  // seu próprio balde, com data de retomada para novo contato.
  if (c.status === "sem_interesse") return { id: "sem_interesse", label: "🚫 Sem interesse", color: "#d97706", pend, docsPend };
  if (c.status === "concluido") return { id: "concluido", label: "✅ Concluída", color: "#059669", pend, docsPend };
  if (!assinado) return { id: "pendente", label: "⏳ Pendente", color: "#f59e0b", pend, docsPend };
  return { id: "andamento", label: "🔄 Em andamento", color: "#3b82f6", pend, docsPend };
};

// Link de WhatsApp a partir de um telefone BR (adiciona DDI 55)
export const waLink = (phone) => {
  if (!phone) return null;
  let d = String(phone).replace(/\D/g, "");
  if (!d) return null;
  if (d.length <= 11) d = "55" + d;
  return `https://wa.me/${d}`;
};

// ── Compressão/redimensionamento de imagem (evita crash por memória no tablet) ──
// Reduz a foto da câmera (12MP+) para no máx. `maxDim` px no maior lado e re-codifica
// em JPEG. Processa uma imagem por vez para manter o pico de memória baixo.
export const compressImage = async (file, maxDim = 1600, quality = 0.72) => {
  if (!file || !(file.type || "").startsWith("image/")) return file; // PDFs/outros: inalterados
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    canvas.width = 0;
    canvas.height = 0; // libera memória do canvas
    if (!blob) return file;
    const base = (file.name || "foto").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // se algo falhar, usa o arquivo original
  }
};

// ── Consulta CEP via ViaCEP ──
export const fetchCEP = async (cep) => {
  const digits = cep.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!res.ok) return null;
    const d = await res.json();
    if (d.erro) return null;
    return {
      rua: d.logradouro || "",
      bairro: d.bairro || "",
      cidade: d.localidade || "",
      uf: d.uf || "",
    };
  } catch {
    return null;
  }
};

// ── Consulta CPF (nome + data de nascimento) via provedor configurável ──
// Não existe API pública/gratuita da Receita para CPF (dado protegido/LGPD).
// REACT_APP_CPF_API_URL (use {cpf} como placeholder) aponta para um provedor
// que NÃO exige chave. Chave de provedor pago nunca entra aqui: tudo que é
// REACT_APP_* vai no JavaScript público do site (auditoria CRM-08, 21/10/2026).
// Provedor com chave precisa de uma função em api/ que guarde a chave e só
// atenda a equipe ou uma ficha já gravada — a busca da ficha pública acontece
// antes de a ficha existir, então um proxy aberto pagaria consulta para
// qualquer um. Em 21/10/2026 nenhuma das duas variáveis estava no site publicado.
export const fetchCPF = async (cpf) => {
  const digits = (cpf || "").replace(/\D/g, "");
  if (digits.length !== 11) return null;
  const base = process.env.REACT_APP_CPF_API_URL;
  if (!base) return { error: "not_configured" };
  try {
    const url = base.includes("{cpf}") ? base.replace("{cpf}", digits) : `${base}${digits}`;
    const res = await fetch(url);
    if (!res.ok) return { error: "http" };
    const d = await res.json();
    const nome = d.nome || d.name || d.nomeCompleto || d.razao_social || "";
    const nascimento = d.nascimento || d.data_nascimento || d.dataNascimento || d.birthdate || d.dt_nascimento || "";
    return { nome, nascimento };
  } catch {
    return { error: "network" };
  }
};

export const exportCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(";"),
    ...data.map(row =>
      headers.map(h => {
        let val = row[h];
        if (Array.isArray(val)) val = val.join(", ");
        if (typeof val === "object" && val !== null) val = JSON.stringify(val);
        if (val === null || val === undefined) val = "";
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(";")
    ),
  ];
  const csvString = "\uFEFF" + csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${getToday()}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};
